import React, { useEffect, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { Header } from 'components/Header';
import { BackButton } from 'components/Buttons';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';
import { useNavigate } from 'react-router-dom';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { automationTasksTrackingEventMapper } from 'services/candidate/utils/automationTrackingEventMapper';
import { RulesetForm } from 'modules/analyse/components/RulesetForm';
import { SubmittedRulesetFormValues } from 'modules/analyse/types/RulesetFormValues';
import { useInitializeCandidateBatchGroupMutation } from 'generated-graphql/hooks/apollo-hooks';
import { validateAnalysisAutomationTasks } from 'modules/analyse/utils/validateAutomationTasks';
import { AutomationTaskInput } from 'generated-graphql/types';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';

const container: SxProps = {
  p: 4
};

const bodyContainer: SxProps = {
  mt: 4
};

export const CandidateRankingGroupCreationPage = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { track } = useTracking();
  const organizationConfig = useOrganizationConfig();

  const [initializeCandidateBatchGroupMutation] =
    useInitializeCandidateBatchGroupMutation();

  const [templateId, setTemplateId] = useState<string | undefined>();

  useEffect(() => {
    if (!templateId && organizationConfig?.defaultAutomationTaskTemplateId) {
      setTemplateId(organizationConfig.defaultAutomationTaskTemplateId);
    }
  }, [organizationConfig]);

  const onFormCompleted = async ({
    groupName,
    analysisRequirements,
    actionAutomationTasks,
    ruleAutomationTasks
  }: SubmittedRulesetFormValues) => {
    const automationTasks = [...actionAutomationTasks, ...ruleAutomationTasks];

    try {
      const { isAllTasksValid, isDuplicates, validAutomationTasks } =
        validateAnalysisAutomationTasks(automationTasks || []);

      if (!isAllTasksValid) {
        showToast({
          message: 'You have some invalid automation tasks',
          type: ToastType.Error
        });
        return;
      }

      if (isDuplicates) {
        showToast({
          message:
            'You have conflicting automations. Remove them before saving.',
          type: ToastType.Error
        });
        return;
      }

      const formattedAutomationTasks =
        validAutomationTasks.map<AutomationTaskInput>((item) => {
          return {
            action: item.action,
            requirementConditions: item.requirementConditions,
            isParent: item.isParent
          };
        });

      track(
        TrackingEventName.RulesetCreate,
        automationTasksTrackingEventMapper(validAutomationTasks)
      );

      const { data } = await initializeCandidateBatchGroupMutation({
        variables: {
          input: {
            name: groupName!,
            automationTasks: formattedAutomationTasks,
            analysisRequirements
          }
        }
      });

      const id = data?.initializeCandidateBatchGroup?.id;

      navigate(`${Paths.ANALYSE_RULESETS}/${id}`);

      showToast({
        message: 'Your ruleset has been created',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({ message: 'Something went wrong', type: ToastType.Error });
    }
  };

  return (
    <Box sx={container}>
      <Header
        title="Create Ruleset"
        subtitle="Rank and shortlist candidates when they’re added to a role"
        prevPagePath={Paths.ANALYSE}
        prevPageTitle={'Analyse'}
        currentPageTitle={'Create Ruleset'}
      />
      <BackButton containerStyle={{ mb: 2 }} />
      <Box sx={bodyContainer}>
        <RulesetForm
          onFormSubmitted={onFormCompleted}
          autofillDefaultTemplate
          templateId={templateId}
          onTemplateChange={(templateId) => {
            setTemplateId(templateId);
          }}
        />
      </Box>
    </Box>
  );
};
